import type { ReactNode } from 'react';
import { matchesSearch } from '#src/shared/text';
import { AsyncCombobox, type AsyncOptions } from '#src/shared/ui/AsyncCombobox';

/** Uma opção de lista local. */
export interface SelectOption<V extends string = string> {
  readonly value: V;
  readonly label: string;
  /** Linha secundária na lista (e no cartão do item escolhido). */
  readonly description?: string;
  /** Motivo para não poder escolher (`Desativada`). */
  readonly disabledReason?: string;
}

export interface SelectFieldProps<V extends string> {
  readonly label: string;
  readonly placeholder: string;
  readonly options: readonly SelectOption<V>[];
  /** `null`: nada escolhido (num filtro, "todos"). */
  readonly value: V | null;
  onChange(value: V | null): void;
  readonly required?: boolean;
  readonly hint?: string | null;
  readonly validationMessage?: string | null;
  readonly disabled?: boolean;
  readonly icon?: ReactNode;
  readonly emptyText?: string;
}

const keyOf = (option: SelectOption) => option.value;
const labelOf = (option: SelectOption) => option.label;
const descriptionOf = (option: SelectOption) => option.description;
const reasonOf = (option: SelectOption) => option.disabledReason ?? null;

/**
 * Lista local pesquisável: o mesmo campo do \`AsyncCombobox\` (digitar filtra,
 * o item escolhido fica travado), para listas que já estão no cliente, como
 * status, perfis e descrições. Todo campo de escolha do app é pesquisável e
 * se comporta igual, venha a lista do servidor ou não.
 */
export function SelectField<V extends string>({
  options,
  value,
  onChange,
  ...field
}: SelectFieldProps<V>) {
  const selected = options.find((option) => option.value === value) ?? null;
  // Sem hooks dentro: filtrar uma lista local é síncrono.
  const useOptions = (text: string): AsyncOptions<SelectOption<V>> => ({
    items: options.filter((option) =>
      matchesSearch(text, option.label, option.description),
    ),
    isLoading: false,
    error: null,
    hasMore: false,
    loadMore: () => undefined,
    retry: () => undefined,
  });
  return (
    <AsyncCombobox<SelectOption<V>>
      {...field}
      value={selected}
      onChange={(option) => onChange(option?.value ?? null)}
      useOptions={useOptions}
      getKey={keyOf}
      getLabel={labelOf}
      getDescription={descriptionOf}
      disabledReason={reasonOf}
    />
  );
}
